import { readUserComment } from "./userComment";

/** Stored envelopes retain optional and future fields without normalizing them. */
export type GameAnalysisMetadata = Record<string, unknown>;

export type GameAnalysisResult = "1-0" | "0-1" | "1/2-1/2" | "*";

export const preparationAnalysisOutcomes = [
  { code: "opening-not-found", label: "Дебют не найден", className: "text-muted-foreground italic" },
  { code: "game-ended-early", label: "Партия закончилась раньше", className: "text-muted-foreground italic" },
  { code: "variation-completed", label: "Дошли до конца варианта", className: "font-bold text-green-600" },
  { code: "student-deviated", label: "Игрок вышел из подготовки", className: "font-bold text-red-600" },
  { code: "opponent-deviated", label: "Соперник вышел из подготовки", className: "font-bold text-yellow-600" },
] as const;
export type PreparationAnalysisOutcome = typeof preparationAnalysisOutcomes[number]["code"];
export type PreparationAnalysis = Readonly<{ outcome: PreparationAnalysisOutcome; comment: string | null }>;

export function isPreparationAnalysisOutcome(value: unknown): value is PreparationAnalysisOutcome {
  return preparationAnalysisOutcomes.some(({ code }) => code === value);
}

function normalizeAnalysis(value: unknown): PreparationAnalysis | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return null;
  const analysis = value as Record<string, unknown>;
  if (!isPreparationAnalysisOutcome(analysis.outcome)) return null;
  return { outcome: analysis.outcome,
    comment: typeof analysis.comment === "string" && analysis.comment.trim() ? analysis.comment : null };
}

export function isPositiveSafeId(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value > 0;
}

export function isCanonicalUploadTime(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) && new Date(milliseconds).toISOString() === value;
}

export function normalizeListMetadata(metadata: GameAnalysisMetadata | null) {
  const studentColor: "white" | "black" | null = metadata?.studentColor === "white" || metadata?.studentColor === "black"
    ? metadata.studentColor : null;
  return {
    uploadedAt: isCanonicalUploadTime(metadata?.uploadedAt) ? metadata.uploadedAt : null,
    studentColor,
    studentScore: deriveStudentScore(studentColor, metadata?.result),
    openingId: isPositiveSafeId(metadata?.openingId) ? metadata.openingId : null,
    analysis: normalizeAnalysis(metadata?.analysis),
    archived: metadata?.archived === true,
    favorite: metadata?.favorite === true,
    userComment: readUserComment(metadata?.userComment),
  };
}

export function deriveStudentScore(studentColor: "white" | "black" | null, result: unknown): 0 | 0.5 | 1 | null {
  if (studentColor === null) return null;
  if (result === "1/2-1/2") return 0.5;
  if (result !== "1-0" && result !== "0-1") return null;
  return (studentColor === "white") === (result === "1-0") ? 1 : 0;
}
